(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-fajmc]';
  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const controllers = new WeakMap();

  class FAJModernCollection {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.collectionHandle = root.dataset.collectionHandle;
      this.ajaxEnabled = root.dataset.ajax === 'true';
      this.searchMode = root.dataset.searchMode || 'current_collection';
      this.defaultView = root.dataset.defaultView || 'grid';
      this.productsPerPage =
        Number.parseInt(root.dataset.productsPerPage, 10) || 24;
      this.paginationMode = root.dataset.paginationMode || 'standard';
      this.searchParam = root.dataset.searchParam || 'collection_q';
      this.searchPageParam =
        root.dataset.searchPageParam || 'collection_search_page';
      this.searchConcurrency = Math.max(
        2,
        Number.parseInt(root.dataset.searchConcurrency, 10) || 4
      );
      this.searchDebounce = Math.max(
        100,
        Number.parseInt(root.dataset.searchDebounce, 10) || 250
      );
      this.abortController = null;
      this.collectionIndexAbort = null;
      this.collectionIndexPromises = new Map();
      this.collectionIndexCache = new Map();
      this.collectionSearchRequestId = 0;
      this.searchInputTimer = null;
      this.priceTimer = null;
      this.infiniteObserver = null;
      this.collectionSearchObserver = null;
      this.collectionSearchState = null;
      this.lastFocusedElement = null;

      this.handleClick = this.handleClick.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handleInput = this.handleInput.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handlePopState = this.handlePopState.bind(this);
      this.handleCollectionBranchToggle =
        this.handleCollectionBranchToggle.bind(this);
      this.handleCollectionNavigationToggle =
        this.handleCollectionNavigationToggle.bind(this);

      this.bind();
      this.applyStoredView();

      if (!this.restoreCollectionSearchFromUrl()) {
        this.initializeInfiniteScroll();
      }
    }

    bind() {
      this.root.addEventListener('click', this.handleClick);
      this.root.addEventListener('change', this.handleChange);
      this.root.addEventListener('input', this.handleInput);
      this.root.addEventListener('submit', this.handleSubmit);
      window.addEventListener('popstate', this.handlePopState);

      this.bindCollectionAccordions({ restoreActive: true });
    }

    destroy() {
      this.root.removeEventListener('click', this.handleClick);
      this.root.removeEventListener('change', this.handleChange);
      this.root.removeEventListener('input', this.handleInput);
      this.root.removeEventListener('submit', this.handleSubmit);
      window.removeEventListener('popstate', this.handlePopState);
      document.removeEventListener('keydown', this.handleKeydown);

      this.unbindCollectionAccordions();
      this.infiniteObserver?.disconnect();
      this.collectionSearchObserver?.disconnect();
      this.abortController?.abort();
      this.collectionIndexAbort?.abort();
      window.clearTimeout(this.searchInputTimer);
      window.clearTimeout(this.priceTimer);
    }

    bindCollectionAccordions({ restoreActive = false } = {}) {
      const navigation = this.root.querySelector(
        '[data-fajmc-collection-navigation]'
      );

      if (navigation) {
        navigation.removeEventListener(
          'toggle',
          this.handleCollectionNavigationToggle
        );
        navigation.addEventListener(
          'toggle',
          this.handleCollectionNavigationToggle
        );
      }

      this.root
        .querySelectorAll('[data-fajmc-collection-branch]')
        .forEach((branch) => {
          branch.removeEventListener(
            'toggle',
            this.handleCollectionBranchToggle
          );
          branch.addEventListener(
            'toggle',
            this.handleCollectionBranchToggle
          );
        });

      if (restoreActive) {
        this.restoreActiveCollectionPath();
      }
    }

    restoreActiveCollectionPath() {
      const navigation = this.root.querySelector(
        '[data-fajmc-collection-navigation]'
      );

      if (!navigation) return;

      navigation.open = true;

      this.root
        .querySelectorAll('[data-fajmc-collection-branch]')
        .forEach((branch) => {
          branch.open = branch.hasAttribute('data-fajmc-active-branch');
        });
    }

    unbindCollectionAccordions() {
      const navigation = this.root.querySelector(
        '[data-fajmc-collection-navigation]'
      );

      navigation?.removeEventListener(
        'toggle',
        this.handleCollectionNavigationToggle
      );

      this.root
        .querySelectorAll('[data-fajmc-collection-branch]')
        .forEach((branch) => {
          branch.removeEventListener(
            'toggle',
            this.handleCollectionBranchToggle
          );
        });
    }

    closeCollectionBranch(branch) {
      if (!branch) return;

      branch.open = false;
      branch
        .querySelectorAll('[data-fajmc-collection-branch]')
        .forEach((descendant) => {
          descendant.open = false;
        });
    }

    handleCollectionNavigationToggle(event) {
      const navigation = event.currentTarget;

      if (!navigation.open) {
        navigation
          .querySelectorAll('[data-fajmc-collection-branch]')
          .forEach((branch) => {
            branch.open = false;
          });
        return;
      }

      const hasOpenBranch = Array.from(
        navigation.querySelectorAll('[data-fajmc-collection-branch]')
      ).some((branch) => branch.open);

      if (!hasOpenBranch) {
        this.restoreActiveCollectionPath();
      }
    }

    handleCollectionBranchToggle(event) {
      const branch = event.currentTarget;

      if (!branch.open) {
        branch
          .querySelectorAll('[data-fajmc-collection-branch]')
          .forEach((descendant) => {
            descendant.open = false;
          });
        return;
      }

      const currentItem = branch.parentElement;
      const list = currentItem?.parentElement;
      if (!list) return;

      Array.from(list.children).forEach((item) => {
        const sibling = item.querySelector(
          ':scope > [data-fajmc-collection-branch]'
        );

        if (sibling && sibling !== branch) {
          this.closeCollectionBranch(sibling);
        }
      });
    }

    handleClick(event) {
      const openButton = event.target.closest('[data-fajmc-open-sidebar]');
      if (openButton) {
        this.openSidebar(openButton);
        return;
      }

      if (
        event.target.closest('[data-fajmc-close-sidebar]') ||
        event.target.closest('[data-fajmc-drawer-overlay]')
      ) {
        this.closeSidebar();
        return;
      }

      const clearSearch = event.target.closest('[data-fajmc-clear-search]');
      if (clearSearch) {
        event.preventDefault();
        this.clearCollectionSearch();
        return;
      }

      const searchPageButton = event.target.closest(
        '[data-fajmc-search-page]'
      );
      if (searchPageButton) {
        event.preventDefault();
        this.showCollectionSearchPage(
          Number.parseInt(searchPageButton.dataset.fajmcSearchPage, 10) || 1,
          { pushState: true, scrollToProducts: true }
        );
        return;
      }

      const searchLoadMore = event.target.closest(
        '[data-fajmc-search-load-more]'
      );
      if (searchLoadMore) {
        event.preventDefault();
        this.loadMoreCollectionSearch();
        return;
      }

      const viewButton = event.target.closest('[data-fajmc-view]');
      if (viewButton) {
        this.setView(viewButton.dataset.fajmcView);
        return;
      }

      const filterLink = event.target.closest(
        '[data-fajmc-filter-link], [data-fajmc-clear-all], [data-fajmc-page-link]'
      );
      if (filterLink && this.ajaxEnabled) {
        event.preventDefault();

        const targetUrl = this.preserveCollectionSearchInUrl(
          filterLink.href
        );

        this.fetchAndReplace(targetUrl, {
          pushState: true,
          scrollToProducts: true
        });
        return;
      }

      const loadMore = event.target.closest('[data-fajmc-load-more]');
      if (loadMore) {
        event.preventDefault();
        this.loadMore(loadMore);
      }
    }

    handleChange(event) {
      const sort = event.target.closest('[data-fajmc-sort]');
      if (sort) {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', sort.value);
        url.searchParams.delete('page');

        if (this.ajaxEnabled) {
          this.fetchAndReplace(url.toString(), {
            pushState: true,
            scrollToProducts: true
          });
        } else {
          window.location.assign(url.toString());
        }
        return;
      }

      const filterForm = event.target.closest('[data-fajmc-filter-form]');
      if (!filterForm) return;

      const drawerNeedsApply =
        this.root.classList.contains('is-sidebar-open') &&
        window.matchMedia('(max-width: 1099px)').matches;

      if (drawerNeedsApply) return;

      const isPriceInput = event.target.matches('input[type="number"]');

      if (isPriceInput) {
        window.clearTimeout(this.priceTimer);
        this.priceTimer = window.setTimeout(() => {
          this.submitFilterForm(filterForm);
        }, 650);
      } else if (event.target.matches('input[type="checkbox"], select')) {
        this.submitFilterForm(filterForm);
      }
    }

    handleInput(event) {
      const menuSearch = event.target.closest('[data-fajmc-menu-search]');
      if (menuSearch) {
        this.filterCollectionMenu(menuSearch.value);
        return;
      }

      const productSearch = event.target.closest(
        '[data-fajmc-product-search]'
      );

      if (!productSearch) return;

      const query = productSearch.value;
      this.updateSearchClearButton(query);

      if (this.searchMode === 'current_page') {
        this.filterVisibleProducts(query);
        return;
      }

      if (this.searchMode === 'current_collection') {
        this.scheduleCollectionSearch(query);
      }
    }

    handleSubmit(event) {
      const filterForm = event.target.closest('[data-fajmc-filter-form]');
      if (filterForm) {
        if (this.ajaxEnabled) {
          event.preventDefault();
          this.submitFilterForm(filterForm);
        }
        return;
      }

      const productSearchForm = event.target.closest(
        '[data-fajmc-product-search-form]'
      );

      if (productSearchForm && this.searchMode !== 'store_search') {
        event.preventDefault();

        const input = productSearchForm.querySelector(
          '[data-fajmc-product-search]'
        );
        const query = input?.value.trim() || '';
        window.clearTimeout(this.searchInputTimer);

        if (this.searchMode === 'current_page') {
          this.filterVisibleProducts(query);
        } else if (query) {
          this.startCollectionSearch(query, {
            pushState: true,
            page: 1,
            scrollToProducts: false
          });
        } else {
          this.clearCollectionSearch();
        }

        return;
      }

      const addForm = event.target.closest('[data-fajmc-add-form]');
      if (addForm) {
        event.preventDefault();
        this.addToCart(addForm);
      }
    }

    handleKeydown(event) {
      if (
        event.key === 'Escape' &&
        this.root.classList.contains('is-sidebar-open')
      ) {
        event.preventDefault();
        this.closeSidebar();
      }
    }

    handlePopState() {
      if (this.ajaxEnabled) {
        this.fetchAndReplace(window.location.href, {
          pushState: false,
          scrollToProducts: false
        });
      }
    }

    submitFilterForm(form) {
      const formData = new FormData(form);
      const url = new URL(form.action, window.location.origin);

      formData.forEach((value, key) => {
        if (String(value).trim() !== '') {
          url.searchParams.append(key, value);
        }
      });

      url.searchParams.delete('page');

      const collectionQuery = this.getCollectionSearchQuery();
      if (collectionQuery && this.searchMode === 'current_collection') {
        url.searchParams.set(this.searchParam, collectionQuery);
        url.searchParams.delete(this.searchPageParam);
      }

      if (this.ajaxEnabled) {
        this.fetchAndReplace(url.toString(), {
          pushState: true,
          scrollToProducts: false
        });
      } else {
        window.location.assign(url.toString());
      }
    }

    async fetchAndReplace(url, options = {}) {
      const { pushState = true, scrollToProducts = false } = options;
      const requestUrl = new URL(url, window.location.origin);
      const publicUrl = new URL(url, window.location.origin);

      requestUrl.searchParams.set('section_id', this.sectionId);
      publicUrl.searchParams.delete('section_id');

      const currentIndexKey = this.createCollectionIndexKey(
        window.location.href
      );
      const nextIndexKey = this.createCollectionIndexKey(
        publicUrl.toString()
      );

      if (currentIndexKey !== nextIndexKey) {
        this.resetCollectionIndex();
      }

      this.collectionSearchObserver?.disconnect();
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.setLoading(true);

      try {
        const response = await fetch(requestUrl.toString(), {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          signal: this.abortController.signal
        });

        if (!response.ok) {
          throw new Error(`Section request failed with ${response.status}`);
        }

        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const nextInner = documentFragment.querySelector('[data-fajmc-section-inner]');
        const currentInner = this.root.querySelector('[data-fajmc-section-inner]');

        if (!nextInner || !currentInner) {
          throw new Error('Collection section markup was not found.');
        }

        currentInner.replaceWith(nextInner);

        if (pushState) {
          const publicUrl = new URL(url, window.location.origin);
          publicUrl.searchParams.delete('section_id');
          window.history.pushState({}, '', publicUrl.toString());
        }

        this.applyStoredView();
        this.bindCollectionAccordions({ restoreActive: true });
        this.closeSidebar({ restoreFocus: false });

        const collectionQuery = this.getCollectionSearchQuery();
        this.syncCollectionSearchInput(collectionQuery);

        if (
          collectionQuery &&
          this.searchMode === 'current_collection'
        ) {
          await this.startCollectionSearch(collectionQuery, {
            pushState: false,
            page: this.getCollectionSearchPage(),
            scrollToProducts: false
          });
        } else {
          this.root.classList.remove('is-collection-search-active');
          this.initializeInfiniteScroll();

          const searchInput = this.root.querySelector(
            '[data-fajmc-product-search]'
          );
          if (searchInput?.value && this.searchMode === 'current_page') {
            this.filterVisibleProducts(searchInput.value);
          }
        }

        if (scrollToProducts) {
          this.root.querySelector('.fajmc__main')?.scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
              ? 'auto'
              : 'smooth',
            block: 'start'
          });
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('[FAJ collection]', error);
          window.location.assign(url);
        }
      } finally {
        this.setLoading(false);
      }
    }

    async loadMore(buttonOrSentinel) {
      if (
        this.collectionSearchState ||
        this.root.classList.contains('is-collection-search-indexing')
      ) {
        return;
      }

      const nextUrl = buttonOrSentinel.dataset.nextUrl;
      if (!nextUrl || buttonOrSentinel.dataset.loading === 'true') return;

      buttonOrSentinel.dataset.loading = 'true';
      buttonOrSentinel.disabled = true;

      const requestUrl = new URL(nextUrl, window.location.origin);
      requestUrl.searchParams.set('section_id', this.sectionId);

      try {
        const response = await fetch(requestUrl.toString(), {
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });

        if (!response.ok) {
          throw new Error(`Pagination request failed with ${response.status}`);
        }

        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const nextGrid = documentFragment.querySelector('[data-fajmc-product-grid]');
        const currentGrid = this.root.querySelector('[data-fajmc-product-grid]');
        const nextPagination = documentFragment.querySelector('[data-fajmc-pagination]');
        const currentPagination = this.root.querySelector('[data-fajmc-pagination]');

        if (!nextGrid || !currentGrid) {
          throw new Error('Next product grid was not found.');
        }

        Array.from(nextGrid.children).forEach((card) => {
          currentGrid.append(card);
        });

        if (currentPagination) {
          if (nextPagination) {
            currentPagination.replaceWith(nextPagination);
          } else {
            currentPagination.remove();
          }
        }

        const publicUrl = new URL(nextUrl, window.location.origin);
        window.history.replaceState({}, '', publicUrl.toString());

        this.initializeInfiniteScroll();

        const searchInput = this.root.querySelector('[data-fajmc-product-search]');
        if (searchInput?.value && this.searchMode === 'current_page') {
          this.filterVisibleProducts(searchInput.value);
        }
      } catch (error) {
        console.error('[FAJ collection pagination]', error);
        buttonOrSentinel.dataset.loading = 'false';
        buttonOrSentinel.disabled = false;
      }
    }

    initializeInfiniteScroll() {
      this.infiniteObserver?.disconnect();

      if (this.collectionSearchState) return;

      const sentinel = this.root.querySelector('[data-fajmc-infinite][data-next-url]');
      if (!sentinel || !('IntersectionObserver' in window)) return;

      this.infiniteObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.infiniteObserver.disconnect();
              this.loadMore(sentinel);
            }
          });
        },
        { rootMargin: '500px 0px' }
      );

      this.infiniteObserver.observe(sentinel);
    }

    normalizeSearchValue(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
    }

    getCollectionSearchQuery(url = window.location.href) {
      const parsed = new URL(url, window.location.origin);
      return parsed.searchParams.get(this.searchParam)?.trim() || '';
    }

    getCollectionSearchPage(url = window.location.href) {
      const parsed = new URL(url, window.location.origin);
      return Math.max(
        1,
        Number.parseInt(
          parsed.searchParams.get(this.searchPageParam),
          10
        ) || 1
      );
    }

    preserveCollectionSearchInUrl(url) {
      const parsed = new URL(url, window.location.origin);
      const query = this.getCollectionSearchQuery();

      if (
        query &&
        this.searchMode === 'current_collection'
      ) {
        parsed.searchParams.set(this.searchParam, query);
        parsed.searchParams.delete(this.searchPageParam);
      }

      return parsed.toString();
    }

    buildCollectionSearchPublicUrl(query, page = 1) {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      url.searchParams.delete('section_id');

      if (query) {
        url.searchParams.set(this.searchParam, query);

        if (page > 1) {
          url.searchParams.set(this.searchPageParam, String(page));
        } else {
          url.searchParams.delete(this.searchPageParam);
        }
      } else {
        url.searchParams.delete(this.searchParam);
        url.searchParams.delete(this.searchPageParam);
      }

      return url;
    }

    createCollectionIndexKey(urlValue = window.location.href) {
      const url = new URL(urlValue, window.location.origin);

      Array.from(url.searchParams.keys()).forEach((key) => {
        if (
          key === this.searchParam ||
          key === this.searchPageParam ||
          key === 'page' ||
          key === 'section_id'
        ) {
          url.searchParams.delete(key);
        }
      });

      const sortedParams = Array.from(url.searchParams.entries())
        .sort(([leftKey, leftValue], [rightKey, rightValue]) => {
          const keyComparison = leftKey.localeCompare(rightKey);
          return keyComparison || leftValue.localeCompare(rightValue);
        });

      url.search = '';
      sortedParams.forEach(([key, value]) => {
        url.searchParams.append(key, value);
      });

      return [
        this.collectionHandle,
        url.pathname,
        url.search
      ].join('|');
    }

    resetCollectionIndex() {
      this.collectionIndexAbort?.abort();
      this.collectionIndexAbort = null;
      this.collectionIndexPromises.clear();
      this.collectionIndexCache.clear();
      this.collectionSearchRequestId += 1;
      this.collectionSearchState = null;
      this.root.classList.remove(
        'is-collection-search-active',
        'is-collection-search-indexing'
      );
    }

    syncCollectionSearchInput(query) {
      const input = this.root.querySelector(
        '[data-fajmc-product-search]'
      );

      if (input) input.value = query || '';
      this.updateSearchClearButton(query);
    }

    updateSearchClearButton(value) {
      const clearButton = this.root.querySelector(
        '[data-fajmc-clear-search]'
      );

      if (clearButton) {
        clearButton.hidden = !String(value || '').trim();
      }
    }

    scheduleCollectionSearch(rawQuery) {
      const query = String(rawQuery || '').trim();
      window.clearTimeout(this.searchInputTimer);

      if (!query) {
        this.searchInputTimer = window.setTimeout(() => {
          this.clearCollectionSearch();
        }, 120);
        return;
      }

      this.searchInputTimer = window.setTimeout(() => {
        const currentUrlQuery = this.getCollectionSearchQuery();
        const nextUrl = this.buildCollectionSearchPublicUrl(query, 1);

        if (currentUrlQuery) {
          window.history.replaceState({}, '', nextUrl.toString());
        } else {
          window.history.pushState({}, '', nextUrl.toString());
        }

        this.startCollectionSearch(query, {
          pushState: false,
          page: 1,
          scrollToProducts: false
        });
      }, this.searchDebounce);
    }

    restoreCollectionSearchFromUrl() {
      if (this.searchMode !== 'current_collection') return false;

      const query = this.getCollectionSearchQuery();
      this.syncCollectionSearchInput(query);

      if (!query) return false;

      this.startCollectionSearch(query, {
        pushState: false,
        page: this.getCollectionSearchPage(),
        scrollToProducts: false
      });

      return true;
    }

    async clearCollectionSearch() {
      window.clearTimeout(this.searchInputTimer);
      this.collectionSearchRequestId += 1;
      this.collectionSearchObserver?.disconnect();
      this.collectionSearchState = null;
      this.root.classList.remove('is-collection-search-active');
      this.syncCollectionSearchInput('');

      const url = this.buildCollectionSearchPublicUrl('', 1);
      url.searchParams.delete('page');

      if (this.ajaxEnabled) {
        await this.fetchAndReplace(url.toString(), {
          pushState: true,
          scrollToProducts: false
        });
      } else {
        window.location.assign(url.toString());
      }
    }

    async startCollectionSearch(
      query,
      {
        pushState = true,
        page = 1,
        scrollToProducts = false
      } = {}
    ) {
      const normalizedQuery = this.normalizeSearchValue(query);

      if (!normalizedQuery) {
        await this.clearCollectionSearch();
        return;
      }

      const requestId = ++this.collectionSearchRequestId;
      const indexKey = this.createCollectionIndexKey();

      this.syncCollectionSearchInput(query);
      this.root.classList.add('is-collection-search-active');
      this.collectionSearchObserver?.disconnect();

      const cachedIndex = this.collectionIndexCache.get(indexKey);
      const seedIndex =
        cachedIndex ||
        this.getCardIndexItems(
          this.root.querySelector('[data-fajmc-product-grid]')
        );

      this.renderMatchesFromIndex(
        seedIndex,
        query,
        normalizedQuery,
        page,
        {
          pushState,
          scrollToProducts
        }
      );

      if (cachedIndex) {
        this.root.classList.remove('is-collection-search-indexing');
        this.setSearchLoading(false);
        return;
      }

      this.root.classList.add('is-collection-search-indexing');
      this.setSearchLoading(true);

      try {
        const completeIndex = await this.ensureCollectionIndex(indexKey);

        if (requestId !== this.collectionSearchRequestId) return;

        this.renderMatchesFromIndex(
          completeIndex,
          query,
          normalizedQuery,
          page,
          {
            pushState: false,
            scrollToProducts: false
          }
        );
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('[FAJ collection search]', error);
          this.renderCollectionSearchError();
        }
      } finally {
        if (requestId === this.collectionSearchRequestId) {
          this.root.classList.remove('is-collection-search-indexing');
          this.setSearchLoading(false);
        }
      }
    }

    renderMatchesFromIndex(
      indexItems,
      query,
      normalizedQuery,
      page,
      {
        pushState = false,
        scrollToProducts = false
      } = {}
    ) {
      const matches = this.filterCollectionIndex(
        indexItems,
        normalizedQuery
      );

      this.collectionSearchState = {
        key: this.createCollectionIndexKey(),
        query: query.trim(),
        normalizedQuery,
        matches,
        currentPage: Math.max(1, page),
        loadedPage: Math.max(1, page)
      };

      this.showCollectionSearchPage(page, {
        pushState,
        scrollToProducts
      });
    }

    filterCollectionIndex(indexItems, normalizedQuery) {
      const queryTokens = normalizedQuery.split(' ').filter(Boolean);

      return indexItems
        .filter((item) =>
          queryTokens.every((token) =>
            item.searchTerms.includes(token)
          )
        )
        .map((item) => item.html);
    }

    async ensureCollectionIndex(indexKey) {
      const cachedIndex = this.collectionIndexCache.get(indexKey);
      if (cachedIndex) return cachedIndex;

      const pendingIndex = this.collectionIndexPromises.get(indexKey);
      if (pendingIndex) return pendingIndex;

      this.collectionIndexAbort?.abort();
      this.collectionIndexAbort = new AbortController();

      const indexPromise = this.collectCollectionIndex(
        this.collectionIndexAbort.signal
      )
        .then((indexItems) => {
          this.collectionIndexCache.set(indexKey, indexItems);
          this.collectionIndexPromises.delete(indexKey);
          return indexItems;
        })
        .catch((error) => {
          this.collectionIndexPromises.delete(indexKey);
          throw error;
        });

      this.collectionIndexPromises.set(indexKey, indexPromise);
      return indexPromise;
    }

    async collectCollectionIndex(signal) {
      const grid = this.root.querySelector(
        '[data-fajmc-product-grid]'
      );

      if (!grid) {
        throw new Error('Collection product grid was not found.');
      }

      const totalPages = Math.max(
        1,
        Number.parseInt(grid.dataset.totalPages, 10) || 1
      );
      const currentPage = Math.max(
        1,
        Number.parseInt(grid.dataset.currentPage, 10) || 1
      );
      const pageItems = new Array(totalPages);
      const queuedPages = [];

      for (let page = 1; page <= totalPages; page += 1) {
        if (page === currentPage) {
          pageItems[page - 1] = this.getCardIndexItems(grid);
        } else {
          queuedPages.push(page);
        }
      }

      let completedPages = 1;
      this.updateCollectionSearchProgress(
        completedPages,
        totalPages
      );

      const workers = Array.from(
        {
          length: Math.min(
            this.searchConcurrency,
            queuedPages.length
          )
        },
        async () => {
          while (queuedPages.length) {
            if (signal.aborted) {
              throw new DOMException(
                'Collection index aborted.',
                'AbortError'
              );
            }

            const page = queuedPages.shift();
            if (!page) break;

            const pageGrid = await this.fetchCollectionPageGrid(
              page,
              signal
            );

            pageItems[page - 1] = this.getCardIndexItems(pageGrid);

            completedPages += 1;
            this.updateCollectionSearchProgress(
              completedPages,
              totalPages
            );
          }
        }
      );

      await Promise.all(workers);

      const seenProductIds = new Set();

      return pageItems
        .flat()
        .filter(Boolean)
        .filter((item) => {
          const identity = item.productId || item.html;
          if (seenProductIds.has(identity)) return false;
          seenProductIds.add(identity);
          return true;
        });
    }

    getCardIndexItems(grid) {
      if (!grid) return [];

      return Array.from(
        grid.querySelectorAll('[data-fajmc-product-card]')
      ).map((card) => {
        const clone = card.cloneNode(true);
        clone.hidden = false;
        clone.removeAttribute('hidden');

        return {
          productId: card.dataset.productId || '',
          searchTerms: this.normalizeSearchValue(
            card.dataset.searchTerms
          ),
          html: clone.outerHTML
        };
      });
    }

    buildCollectionPageRequestUrl(page) {
      const url = new URL(window.location.href);

      url.searchParams.delete(this.searchParam);
      url.searchParams.delete(this.searchPageParam);
      url.searchParams.delete('section_id');

      if (page > 1) {
        url.searchParams.set('page', String(page));
      } else {
        url.searchParams.delete('page');
      }

      url.searchParams.set('section_id', this.sectionId);
      return url;
    }

    async fetchCollectionPageGrid(page, signal) {
      const requestUrl = this.buildCollectionPageRequestUrl(page);
      const response = await fetch(requestUrl.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal
      });

      if (!response.ok) {
        throw new Error(
          `Collection page ${page} failed with ${response.status}.`
        );
      }

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(
        html,
        'text/html'
      );
      const pageGrid = parsed.querySelector(
        '[data-fajmc-product-grid]'
      );

      if (!pageGrid) {
        throw new Error(
          `Collection product grid was missing on page ${page}.`
        );
      }

      return pageGrid;
    }

    showCollectionSearchPage(
      requestedPage,
      {
        pushState = true,
        scrollToProducts = false
      } = {}
    ) {
      const state = this.collectionSearchState;
      const grid = this.root.querySelector(
        '[data-fajmc-product-grid]'
      );
      const paginationHost = this.root.querySelector(
        '[data-fajmc-pagination-host]'
      );

      if (!state || !grid || !paginationHost) return;

      const totalMatches = state.matches.length;
      const totalPages = Math.max(
        1,
        Math.ceil(totalMatches / this.productsPerPage)
      );
      const page = Math.min(
        Math.max(1, requestedPage || 1),
        totalPages
      );

      state.currentPage = page;

      let visibleMarkup = [];
      if (
        this.paginationMode === 'load_more' ||
        this.paginationMode === 'infinite'
      ) {
        state.loadedPage = page;
        visibleMarkup = state.matches.slice(
          0,
          page * this.productsPerPage
        );
      } else {
        const start = (page - 1) * this.productsPerPage;
        visibleMarkup = state.matches.slice(
          start,
          start + this.productsPerPage
        );
      }

      grid.innerHTML = visibleMarkup.join('');
      grid.dataset.totalPages = String(totalPages);
      grid.dataset.totalItems = String(totalMatches);
      grid.dataset.currentPage = String(page);

      const emptyState = this.root.querySelector(
        '[data-fajmc-local-empty]'
      );
      if (emptyState) {
        emptyState.hidden = totalMatches !== 0;
      }

      const resultCount = this.root.querySelector(
        '[data-fajmc-result-count]'
      );
      if (resultCount) {
        resultCount.textContent = String(totalMatches);
      }

      paginationHost.innerHTML = this.buildCollectionSearchPagination(
        page,
        totalPages,
        totalMatches
      );

      this.applyStoredView();
      this.initializeCollectionSearchInfiniteScroll();

      if (pushState) {
        const publicUrl = this.buildCollectionSearchPublicUrl(
          state.query,
          page
        );
        window.history.pushState({}, '', publicUrl.toString());
      }

      if (scrollToProducts) {
        this.root.querySelector('.fajmc__toolbar')?.scrollIntoView({
          behavior: window.matchMedia(
            '(prefers-reduced-motion: reduce)'
          ).matches
            ? 'auto'
            : 'smooth',
          block: 'start'
        });
      }
    }

    buildCollectionSearchPagination(
      page,
      totalPages,
      totalMatches
    ) {
      if (!totalMatches || totalPages <= 1) return '';

      if (this.paginationMode === 'load_more') {
        if (page >= totalPages) return '';

        return `
          <div class="fajmc__load-more-wrap" data-fajmc-search-pagination>
            <button
              type="button"
              class="fajmc__load-more"
              data-fajmc-search-load-more
            >
              ${this.escapeHtml(
                this.root.dataset.loadMoreLabel || 'Load more products'
              )}
            </button>
          </div>
        `;
      }

      if (this.paginationMode === 'infinite') {
        if (page >= totalPages) return '';

        return `
          <div
            class="fajmc__infinite-sentinel"
            data-fajmc-search-pagination
            data-fajmc-search-infinite
          >
            <span class="visually-hidden">Load more products</span>
          </div>
        `;
      }

      const pageTokens = this.getPaginationTokens(
        page,
        totalPages
      );

      const pageButtons = pageTokens
        .map((token) => {
          if (token === '…') {
            return '<span class="fajmc__pagination-ellipsis">…</span>';
          }

          const isCurrent = token === page;
          return `
            <button
              type="button"
              data-fajmc-search-page="${token}"
              ${isCurrent ? 'aria-current="page"' : ''}
              aria-label="Page ${token}"
            >
              ${token}
            </button>
          `;
        })
        .join('');

      return `
        <nav
          class="fajmc__pagination"
          aria-label="Collection search pagination"
          data-fajmc-search-pagination
        >
          <button
            type="button"
            data-fajmc-search-page="${Math.max(1, page - 1)}"
            ${page <= 1 ? 'disabled' : ''}
          >
            <svg viewBox="0 0 20 20" aria-hidden="true">
              <path d="m12.5 4.5-5 5 5 5" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>${this.escapeHtml(
              this.root.dataset.previousLabel || 'Previous'
            )}</span>
          </button>

          <div class="fajmc__pagination-parts">
            ${pageButtons}
          </div>

          <button
            type="button"
            data-fajmc-search-page="${Math.min(totalPages, page + 1)}"
            ${page >= totalPages ? 'disabled' : ''}
          >
            <span>${this.escapeHtml(
              this.root.dataset.nextLabel || 'Next'
            )}</span>
            <svg viewBox="0 0 20 20" aria-hidden="true">
              <path d="m7.5 4.5 5 5-5 5" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </nav>
      `;
    }

    getPaginationTokens(currentPage, totalPages) {
      if (totalPages <= 7) {
        return Array.from(
          { length: totalPages },
          (_, index) => index + 1
        );
      }

      const tokens = [1];
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      if (start > 2) tokens.push('…');

      for (let page = start; page <= end; page += 1) {
        tokens.push(page);
      }

      if (end < totalPages - 1) tokens.push('…');
      tokens.push(totalPages);

      return tokens;
    }

    loadMoreCollectionSearch() {
      const state = this.collectionSearchState;
      if (!state) return;

      const totalPages = Math.max(
        1,
        Math.ceil(state.matches.length / this.productsPerPage)
      );
      const nextPage = Math.min(
        totalPages,
        (state.loadedPage || state.currentPage || 1) + 1
      );

      if (nextPage <= (state.loadedPage || 1)) return;

      this.showCollectionSearchPage(nextPage, {
        pushState: true,
        scrollToProducts: false
      });
    }

    initializeCollectionSearchInfiniteScroll() {
      this.collectionSearchObserver?.disconnect();

      if (
        !this.collectionSearchState ||
        this.paginationMode !== 'infinite' ||
        !('IntersectionObserver' in window)
      ) {
        return;
      }

      const sentinel = this.root.querySelector(
        '[data-fajmc-search-infinite]'
      );
      if (!sentinel) return;

      this.collectionSearchObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.collectionSearchObserver.disconnect();
              this.loadMoreCollectionSearch();
            }
          });
        },
        { rootMargin: '500px 0px' }
      );

      this.collectionSearchObserver.observe(sentinel);
    }

    updateCollectionSearchProgress(completed, total) {
      const loadingText = this.root.querySelector(
        '[data-fajmc-loading-text]'
      );
      if (!loadingText) return;

      const progressLabel =
        this.root.dataset.searchProgressLabel ||
        'Checking product pages';

      loadingText.innerHTML = `
        <span class="fajmc__search-progress">
          <span>${this.escapeHtml(progressLabel)}</span>
          <strong>${completed}/${total}</strong>
        </span>
      `;
    }

    setSearchLoading(isLoading) {
      this.setLoading(isLoading);

      if (isLoading) {
        const loadingText = this.root.querySelector(
          '[data-fajmc-loading-text]'
        );

        if (loadingText) {
          loadingText.textContent =
            this.root.dataset.searchingLabel ||
            'Searching this collection…';
        }
      }
    }

    renderCollectionSearchError() {
      const grid = this.root.querySelector(
        '[data-fajmc-product-grid]'
      );
      const paginationHost = this.root.querySelector(
        '[data-fajmc-pagination-host]'
      );

      if (grid) {
        grid.innerHTML = `
          <div class="fajmc__local-search-empty">
            <h2>Collection search unavailable</h2>
            <p>${this.escapeHtml(
              this.root.dataset.searchErrorLabel ||
              'The collection search could not be completed. Please try again.'
            )}</p>
          </div>
        `;
      }

      if (paginationHost) paginationHost.innerHTML = '';
    }

    escapeHtml(value) {
      return String(value || '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    }

    filterCollectionMenu(query) {
      const normalized = query.trim().toLowerCase();
      const items = this.root.querySelectorAll('[data-fajmc-menu-item]');

      items.forEach((item) => {
        const label = item.dataset.menuLabel || '';
        const childMatches = Array.from(
          item.querySelectorAll('[data-fajmc-menu-item]')
        ).some((child) => (child.dataset.menuLabel || '').includes(normalized));

        const matches = !normalized || label.includes(normalized) || childMatches;
        item.hidden = !matches;

        if (normalized && matches) {
          item.closest('details')?.setAttribute('open', '');
          item.querySelector(':scope > details')?.setAttribute('open', '');
        }
      });
    }

    filterVisibleProducts(query) {
      const normalized = query.trim().toLowerCase();
      const cards = Array.from(
        this.root.querySelectorAll('[data-fajmc-product-card]')
      );
      let visibleCount = 0;

      cards.forEach((card) => {
        const terms = card.dataset.searchTerms || '';
        const visible = !normalized || terms.includes(normalized);
        card.hidden = !visible;
        if (visible) visibleCount += 1;
      });

      const emptyState = this.root.querySelector('[data-fajmc-local-empty]');
      if (emptyState) {
        emptyState.hidden = visibleCount !== 0;
      }

      const resultCount = this.root.querySelector('[data-fajmc-result-count]');
      if (resultCount) {
        resultCount.textContent = normalized
          ? String(visibleCount)
          : resultCount.dataset.serverCount || resultCount.textContent;
      }
    }

    setView(view) {
      if (!['grid', 'list'].includes(view)) return;

      const grid = this.root.querySelector('[data-fajmc-product-grid]');
      if (!grid) return;

      grid.classList.remove('fajmc__products--grid', 'fajmc__products--list');
      grid.classList.add(`fajmc__products--${view}`);

      this.root.querySelectorAll('[data-fajmc-view]').forEach((button) => {
        button.setAttribute(
          'aria-pressed',
          button.dataset.fajmcView === view ? 'true' : 'false'
        );
      });

      try {
        localStorage.setItem(`fajmc-view-${this.collectionHandle}`, view);
      } catch (error) {
        // Storage can be unavailable in strict privacy modes.
      }
    }

    applyStoredView() {
      let view = this.defaultView;

      try {
        view =
          localStorage.getItem(`fajmc-view-${this.collectionHandle}`) ||
          this.defaultView;
      } catch (error) {
        view = this.defaultView;
      }

      this.setView(view);

      const count = this.root.querySelector('[data-fajmc-result-count]');
      if (count && !count.dataset.serverCount) {
        count.dataset.serverCount = count.textContent.trim();
      }
    }

    openSidebar(button) {
      const sidebar = this.root.querySelector('[data-fajmc-sidebar]');
      if (!sidebar) return;

      this.lastFocusedElement = button || document.activeElement;
      this.root.classList.add('is-sidebar-open');
      this.root.querySelector('[data-fajmc-open-sidebar]')?.setAttribute(
        'aria-expanded',
        'true'
      );

      document.addEventListener('keydown', this.handleKeydown);

      window.requestAnimationFrame(() => {
        sidebar.querySelector(FOCUSABLE)?.focus({ preventScroll: true });
      });
    }

    closeSidebar({ restoreFocus = true } = {}) {
      if (!this.root.classList.contains('is-sidebar-open')) return;

      this.root.classList.remove('is-sidebar-open');
      this.root.querySelector('[data-fajmc-open-sidebar]')?.setAttribute(
        'aria-expanded',
        'false'
      );

      document.removeEventListener('keydown', this.handleKeydown);

      if (restoreFocus && this.lastFocusedElement?.focus) {
        this.lastFocusedElement.focus({ preventScroll: true });
      }
    }

    setLoading(isLoading) {
      this.root.classList.toggle('is-loading', isLoading);
      const indicator = this.root.querySelector('[data-fajmc-loading]');
      if (indicator) indicator.hidden = !isLoading;
    }

    async addToCart(form) {
      const button = form.querySelector('button[type="submit"]');
      const label = button?.querySelector('span');
      if (!button || !label) return;

      button.disabled = true;
      label.textContent = button.dataset.addingLabel || 'Adding…';

      try {
        const response = await fetch(
          `${window.Shopify?.routes?.root || '/'}cart/add.js`,
          {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: new FormData(form)
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.description || 'Unable to add product.');
        }

        const item = await response.json();
        label.textContent = button.dataset.addedLabel || 'Added';

        const cartResponse = await fetch(
          `${window.Shopify?.routes?.root || '/'}cart.js`,
          { headers: { Accept: 'application/json' } }
        );
        const cart = await cartResponse.json();

        document.querySelectorAll('[data-cart-count]').forEach((element) => {
          element.textContent = cart.item_count;
        });

        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            bubbles: true,
            detail: { cart, item }
          })
        );

        window.setTimeout(() => {
          label.textContent = button.dataset.defaultLabel || 'Add to Cart';
          button.disabled = false;
        }, 1300);
      } catch (error) {
        console.error('[FAJ add to cart]', error);
        label.textContent = button.dataset.defaultLabel || 'Add to Cart';
        button.disabled = false;
        window.alert(error.message);
      }
    }
  }

  function init(root) {
    if (!root || controllers.has(root)) return;
    controllers.set(root, new FAJModernCollection(root));
  }

  function destroy(root) {
    const controller = controllers.get(root);
    if (!controller) return;
    controller.destroy();
    controllers.delete(root);
  }

  function initAll(scope = document) {
    if (scope.matches?.(ROOT_SELECTOR)) init(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.(ROOT_SELECTOR)
      ? event.target
      : event.target.querySelector?.(ROOT_SELECTOR);

    if (root) destroy(root);
  });
})();

(() => {
  const instances = new Map();
  const mediaQuery = window.matchMedia('(min-width: 990px)');

  class FajCollectionStickySidebar {
    constructor(section) {
      this.section = section;
      this.fixedAncestors = [];
      this.onViewportChange = this.onViewportChange.bind(this);
    }

    init() {
      this.prepare();

      if (typeof mediaQuery.addEventListener === 'function') {
        mediaQuery.addEventListener('change', this.onViewportChange);
      } else if (typeof mediaQuery.addListener === 'function') {
        mediaQuery.addListener(this.onViewportChange);
      }
    }

    onViewportChange() {
      this.restoreAncestors();
      this.prepare();
    }

    prepare() {
      if (
        this.section.dataset.stickySidebar !== 'true' ||
        !mediaQuery.matches ||
        !this.section.classList.contains('fajmc--desktop-sidebar-visible') ||
        !this.section.querySelector('[data-fajmc-sidebar-sticky]')
      ) {
        return;
      }

      let ancestor = this.section.parentElement;

      while (ancestor && ancestor !== document.body) {
        const styles = window.getComputedStyle(ancestor);
        const overflowValues = [
          styles.overflow,
          styles.overflowX,
          styles.overflowY
        ];

        const clipsSticky = overflowValues.some((value) =>
          ['hidden', 'clip', 'auto', 'scroll'].includes(value)
        );

        const isActualVerticalScroller =
          ancestor.scrollHeight > ancestor.clientHeight + 2 &&
          ['auto', 'scroll'].includes(styles.overflowY);

        if (clipsSticky && !isActualVerticalScroller) {
          ancestor.classList.add('fajmc-sticky-sidebar-overflow-fix');
          this.fixedAncestors.push(ancestor);
        }

        ancestor = ancestor.parentElement;
      }
    }

    restoreAncestors() {
      this.fixedAncestors.forEach((ancestor) => {
        ancestor.classList.remove('fajmc-sticky-sidebar-overflow-fix');
      });

      this.fixedAncestors = [];
    }

    destroy() {
      this.restoreAncestors();

      if (typeof mediaQuery.removeEventListener === 'function') {
        mediaQuery.removeEventListener('change', this.onViewportChange);
      } else if (typeof mediaQuery.removeListener === 'function') {
        mediaQuery.removeListener(this.onViewportChange);
      }
    }
  }

  const initSection = (section) => {
    if (!section || instances.has(section)) return;

    const instance = new FajCollectionStickySidebar(section);
    instances.set(section, instance);
    instance.init();
  };

  const destroySection = (section) => {
    const instance = instances.get(section);
    if (!instance) return;

    instance.destroy();
    instances.delete(section);
  };

  const initAll = (root = document) => {
    root.querySelectorAll('[data-fajmc]').forEach(initSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-fajmc]').forEach(destroySection);

    if (event.target.matches?.('[data-fajmc]')) {
      destroySection(event.target);
    }
  });
})();


(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-fajsc]';
  const controllers = new WeakMap();

  class FAJServicesCollectionEnhancements {
    constructor(root) {
      this.root = root;
      this.modal = root.querySelector('[data-fajsc-booking-modal]');
      this.form = root.querySelector('[data-fajsc-booking-form]');
      this.success = root.querySelector('[data-fajsc-booking-success]');
      this.status = root.querySelector('[data-fajsc-form-status]');
      this.lastTrigger = null;
      this.observer = null;

      this.handleClick = this.handleClick.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleMutation = this.handleMutation.bind(this);
      this.handleDialogClick = this.handleDialogClick.bind(this);
    }

    init() {
      this.root.classList.add('is-enhanced');
      this.revealCards(this.root);
      this.root.addEventListener('click', this.handleClick);
      this.form?.addEventListener('submit', this.handleSubmit);
      this.modal?.addEventListener('click', this.handleDialogClick);

      this.observer = new MutationObserver(this.handleMutation);
      this.observer.observe(this.root, { childList: true, subtree: true });
    }

    destroy() {
      this.root.removeEventListener('click', this.handleClick);
      this.form?.removeEventListener('submit', this.handleSubmit);
      this.modal?.removeEventListener('click', this.handleDialogClick);
      this.observer?.disconnect();
      this.observer = null;
    }

    handleMutation(mutations) {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            this.revealCards(node);
          }
        });
      });
    }

    revealCards(scope) {
      const cards = [];
      if (scope.matches?.('[data-fajsc-card]')) cards.push(scope);
      scope.querySelectorAll?.('[data-fajsc-card]').forEach((card) => cards.push(card));

      cards.forEach((card, index) => {
        if (card.classList.contains('is-revealed')) return;
        card.style.setProperty('--fajsc-card-index', String(index));
        requestAnimationFrame(() => card.classList.add('is-revealed'));
      });
    }

    handleClick(event) {
      const bookingTrigger = event.target.closest('[data-fajsc-book-service]');
      if (bookingTrigger) {
        event.preventDefault();
        this.openBooking(bookingTrigger);
        return;
      }

      if (event.target.closest('[data-fajsc-close-booking]')) {
        event.preventDefault();
        this.closeBooking();
      }
    }

    handleDialogClick(event) {
      if (event.target === this.modal) this.closeBooking();
    }

    moneyFromCents(cents) {
      const amount = (Number(cents) || 0) / 100;
      const currency = this.root.dataset.currencyCode || 'AED';
      try {
        return new Intl.NumberFormat('en-AE', {
          style: 'currency',
          currency,
          minimumFractionDigits: 2
        }).format(amount);
      } catch (error) {
        return `${currency} ${amount.toFixed(2)}`;
      }
    }

    decimalFromCents(cents) {
      return ((Number(cents) || 0) / 100).toFixed(2);
    }

    setField(name, value) {
      const field = this.form?.querySelector(`[data-fajsc-field="${name}"]`);
      if (field) field.value = value || '';
    }

    openBooking(trigger) {
      if (!this.modal || !this.form) return;

      this.lastTrigger = trigger;
      this.form.reset();
      this.form.hidden = false;
      if (this.success) this.success.hidden = true;
      if (this.status) {
        this.status.textContent = '';
        this.status.className = 'fajsc__form-status';
      }

      const title = trigger.dataset.serviceTitle || 'Service';
      const feeCents = Number(trigger.dataset.feeCents || 0);
      const vatCents = Number(trigger.dataset.vatCents || 0);
      const totalCents = Number(trigger.dataset.totalCents || 0);

      const selected = this.modal.querySelector('[data-fajsc-selected-service]');
      if (selected) selected.textContent = title;

      this.setField('service_product', title);
      this.setField('product_handle', trigger.dataset.productHandle);
      this.setField('product_id', trigger.dataset.productId);
      this.setField('variant_id', trigger.dataset.variantId);
      this.setField('product_url', trigger.dataset.productUrl);
      this.setField('service_category', trigger.dataset.serviceCategory);
      this.setField('inspection_fee', this.decimalFromCents(feeCents));
      this.setField('vat_amount', this.decimalFromCents(vatCents));
      this.setField('estimated_total', this.decimalFromCents(totalCents));
      this.setField('subject', `New Service Booking — ${title}`);

      const feeText = this.moneyFromCents(feeCents);
      const vatText = this.moneyFromCents(vatCents);
      const totalText = this.moneyFromCents(totalCents);
      this.modal.querySelectorAll('[data-fajsc-fee]').forEach((el) => { el.textContent = feeText; });
      this.modal.querySelectorAll('[data-fajsc-vat]').forEach((el) => { el.textContent = vatText; });
      this.modal.querySelectorAll('[data-fajsc-total]').forEach((el) => { el.textContent = totalText; });
      this.modal.querySelectorAll('[data-fajsc-agreement-fee]').forEach((el) => { el.textContent = feeText; });

      if (typeof this.modal.showModal === 'function') this.modal.showModal();
      else this.modal.setAttribute('open', '');
      document.body.classList.add('fajsc-modal-open');
      this.modal.querySelector('input:not([type="hidden"])')?.focus();
    }

    closeBooking() {
      if (!this.modal) return;
      if (typeof this.modal.close === 'function' && this.modal.open) this.modal.close();
      else this.modal.removeAttribute('open');
      document.body.classList.remove('fajsc-modal-open');
      this.lastTrigger?.focus();
    }

    setStatus(message, error = false) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.className = `fajsc__form-status${error ? ' is-error' : ''}`;
    }

    async handleSubmit(event) {
      event.preventDefault();
      if (!this.form) return;

      const endpoint = this.form.action?.trim();
      if (!endpoint || endpoint === window.location.href) {
        this.setStatus('Add the Formspree endpoint in the Theme Editor before submitting.', true);
        return;
      }

      const honeypot = this.form.querySelector('input[name="website"]');
      if (honeypot?.value) return;

      const submit = this.form.querySelector('[data-fajsc-submit-booking]');
      const originalText = submit?.querySelector('span')?.textContent || 'Confirm Booking Request';
      if (submit) {
        submit.disabled = true;
        const label = submit.querySelector('span');
        if (label) label.textContent = 'Sending…';
      }
      this.setStatus('');

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          body: new FormData(this.form),
          headers: { Accept: 'application/json' }
        });

        if (!response.ok) {
          let message = 'The booking request could not be sent. Please check the fields and try again.';
          try {
            const payload = await response.json();
            if (payload?.errors?.length) message = payload.errors.map((item) => item.message).join(' ');
          } catch (error) {}
          throw new Error(message);
        }

        this.form.hidden = true;
        if (this.success) this.success.hidden = false;

        const whatsapp = this.modal.querySelector('[data-fajsc-success-whatsapp]');
        if (whatsapp) {
          const title = this.form.querySelector('[name="service_product"]')?.value || 'Service';
          const name = this.form.querySelector('[name="name"]')?.value || '';
          const phone = this.form.querySelector('[name="phone"]')?.value || '';
          const date = this.form.querySelector('[name="date"]')?.value || '';
          const slot = this.form.querySelector('[name="time_slot"]:checked')?.value || '';
          const total = this.form.querySelector('[name="estimated_total"]')?.value || '';
          const reference = `FAJ-${Date.now().toString().slice(-7)}`;
          const message = [
            'Hello FAJ Support, I submitted a service booking request:',
            `Reference: ${reference}`,
            `Service: ${title}`,
            `Name: ${name}`,
            `Phone: ${phone}`,
            `Preferred Date: ${date}`,
            `Preferred Slot: ${slot}`,
            `Estimated Total: ${this.root.dataset.currencyCode || 'AED'} ${total}`
          ].join('\\n');
          const number = this.root.dataset.whatsappNumber || '';
          whatsapp.href = `https://wa.me/${number}?text=${encodeURIComponent(message)}`;
        }
      } catch (error) {
        this.setStatus(error.message || 'The booking request could not be sent.', true);
      } finally {
        if (submit) {
          submit.disabled = false;
          const label = submit.querySelector('span');
          if (label) label.textContent = originalText;
        }
      }
    }
  }

  const init = (root) => {
    if (!root || controllers.has(root)) return;
    const controller = new FAJServicesCollectionEnhancements(root);
    controllers.set(root, controller);
    controller.init();
  };

  const destroy = (root) => {
    const controller = controllers.get(root);
    if (!controller) return;
    controller.destroy();
    controllers.delete(root);
  };

  const initAll = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) init(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches?.(ROOT_SELECTOR)) destroy(event.target);
    event.target.querySelectorAll?.(ROOT_SELECTOR).forEach(destroy);
  });
})();
